/**
 * Subscriber upload/verify error messaging.
 *
 * As of Orchestrator PR #1839, per-row errors carry a stable `field` code
 * alongside the human-readable `message`. The dashboard turns those codes
 * into actionable, translation-ready strings so the four new error modes
 * surface cleanly instead of dumping the raw API message into a toast.
 *
 * Known codes (keep this list in sync with
 * `src/modules/campaign-subscribers/service.ts` in the orchestrator):
 *
 *  - `subscriber.debtExistsWithExtras`  (409, Flavor-2 REJECT)
 *      Caller sent fields beyond `debt_external_id` for a debt that already
 *      exists. Action: use `PATCH /v1/contacts/:id/debts/:debt_id` to update
 *      the debt, or strip the extras and re-upload.
 *
 *  - `subscriber.contactNotFound`  (404)
 *      `context.contact_id` is missing or belongs to another tenant
 *      (collapsed to avoid tenant leak). Action: drop the contact_id or
 *      verify the UUID belongs to the current account.
 *
 *  - Tier-A schema errors like `context.debts.0.parties` or
 *    `context.debts.0.role`  (400)
 *      Parties/role inside a debt entry were rejected. Action: use
 *      `POST /v1/contacts/:id/debts/:debt_id/parties` to set up
 *      co-borrowers after the debt exists.
 *
 *  - Cross-row shared `debt_external_id`  (400, Tier A)
 *      Two subscriber rows share the same external ID. The server points
 *      at the Debt API parties endpoint for co-borrower setup.
 */

import type { VerifySubscribersResponse } from '../api';

export type SubscriberErrorCode =
  | 'subscriber.debtExistsWithExtras'
  | 'subscriber.contactNotFound'
  | 'subscriber.debtPartiesForbidden'
  | 'subscriber.sharedDebtExternalId'
  | 'subscriber.identityConflict'
  | 'subscriber.intraBatchCollision'
  | 'subscriber.sharedContactId'
  | 'subscriber.identityCollision'
  | 'subscriber.generic';

/**
 * Classify a per-row error into one of the canonical codes above.
 *
 * The orchestrator sets `field` to the code directly for the 409/404 cases.
 * For Tier-A schema errors the `field` is a Joi-style path — we pattern-match
 * on the message text and path segments to pick a bucket.
 */
export const classifySubscriberError = (
  err: VerifySubscribersResponse[number] | undefined
): SubscriberErrorCode => {
  if (!err) {
    return 'subscriber.generic';
  }
  const field = err.field ?? '';
  if (field === 'subscriber.debtExistsWithExtras') {
    return 'subscriber.debtExistsWithExtras';
  }
  if (field === 'subscriber.contactNotFound') {
    return 'subscriber.contactNotFound';
  }
  if (field === 'subscriber.identityConflict') {
    return 'subscriber.identityConflict';
  }
  if (field === 'subscriber.intraBatchCollision') {
    return 'subscriber.intraBatchCollision';
  }
  if (field === 'subscriber.sharedContactId') {
    return 'subscriber.sharedContactId';
  }
  if (field === 'subscriber.identityCollision') {
    return 'subscriber.identityCollision';
  }
  // Parties/role nested inside a debt entry (rejected at schema layer).
  if (/debts\.\d+\.(parties|role)/.test(field)) {
    return 'subscriber.debtPartiesForbidden';
  }
  const message = err.message ?? err.error ?? '';
  // Cross-row shared debt_external_id surfaces as a Tier-A 400 with the
  // external ID echoed in the message and a pointer to the parties endpoint.
  if (/same\s+debt_external_id|shared\s+debt_external_id|parties endpoint/i.test(message)) {
    return 'subscriber.sharedDebtExternalId';
  }
  return 'subscriber.generic';
};

/**
 * Translation-key mapping for each code. Falls back to the raw server
 * message when the code is `generic` (keeps parity with today's behavior).
 */
export const subscriberErrorI18nKey: Record<
  Exclude<SubscriberErrorCode, 'subscriber.generic'>,
  string
> = {
  'subscriber.debtExistsWithExtras': 'campaigns.subscriber_errors.debt_exists_with_extras',
  'subscriber.contactNotFound': 'campaigns.subscriber_errors.contact_not_found',
  'subscriber.debtPartiesForbidden': 'campaigns.subscriber_errors.debt_parties_forbidden',
  'subscriber.sharedDebtExternalId': 'campaigns.subscriber_errors.shared_debt_external_id',
  'subscriber.identityConflict': 'campaigns.subscriber_errors.identity_conflict',
  'subscriber.intraBatchCollision': 'campaigns.subscriber_errors.intra_batch_collision',
  'subscriber.sharedContactId': 'campaigns.subscriber_errors.shared_contact_id',
  'subscriber.identityCollision': 'campaigns.subscriber_errors.identity_collision',
};

/**
 * Resolve a per-row error into a user-facing string.
 *
 * @param err - The raw error entry from `verify` or `bulk-upsert`
 * @param t - i18next translator
 * @returns Localized, action-oriented message. Falls back to the server
 *          `message` / `error` field when the code is unrecognized so no
 *          information is lost.
 */
/** Requirement-scan tokens recoverable from a raw server message when `field` is omitted. */
const REQUIREMENT_FIELD_PATTERNS: Array<[RegExp, string]> = [
  [/debt/i, 'debts'],
  [/country_code/i, 'country_code'],
  [/residence_state/i, 'residence_state'],
];

// A specific `field` is authoritative; the bare `context` container isn't (Tier-A errors carry the real path in the message), and requirement errors omit `field` — infer from the message in both cases.
export const normalizeSubscriberErrorField = (
  err: VerifySubscribersResponse[number] | undefined
): string | undefined => {
  if (err?.field && err.field !== 'context') {
    return err.field;
  }
  const raw = `${err?.message ?? ''} ${err?.error ?? ''}`;
  const hits = REQUIREMENT_FIELD_PATTERNS.filter(([re]) => re.test(raw)).map(([, field]) => field);
  return hits.length > 0 ? hits.join(',') : undefined;
};

/**
 * Map an error field code to the subscriber column keys that should be highlighted.
 * Returns empty array for unknown/generic errors.
 */
export const getErrorColumns = (errorField: string | undefined): string[] => {
  if (!errorField) {
    return [];
  }

  // Handle comma-separated fields from client-side validation
  if (errorField.includes(',')) {
    return [...new Set(errorField.split(',').flatMap((f) => getErrorColumns(f.trim())))];
  }

  const code = errorField as SubscriberErrorCode;

  const codeToColumns: Partial<Record<SubscriberErrorCode, string[]>> = {
    'subscriber.contactNotFound': ['email', 'phone'],
    'subscriber.debtExistsWithExtras': ['debt.debt_external_id'],
    'subscriber.sharedDebtExternalId': ['debt.debt_external_id'],
    'subscriber.identityConflict': ['email', 'phone'],
    'subscriber.intraBatchCollision': ['email', 'phone'],
    'subscriber.identityCollision': ['email', 'phone'],
    'subscriber.sharedContactId': ['email', 'phone'],
    'subscriber.debtPartiesForbidden': ['debt.amount'],
  };

  if (codeToColumns[code]) {
    return codeToColumns[code];
  }

  // Bare requirement code — the debt spec as a whole (chips refine it per row).
  if (errorField === 'debts' || errorField === 'debt') {
    return ['debt.amount', 'debt.currency'];
  }

  // Joi-style paths like "context.debts.0.amount" → "debt.amount"
  const joiMatch = errorField.match(/debts\.\d+\.(\w+)/);
  if (joiMatch) {
    return [`debt.${joiMatch[1]}`];
  }

  // Direct field names from the API (e.g., "email", "phone", "account_id")
  const directFields = [
    'email',
    'phone',
    'account_id',
    'user.display_name',
    'residence_state',
    'country_code',
    'debt.amount',
    'debt.currency',
    'debt.due_date',
    'debt.original_creditor',
    'debt.debt_external_id',
  ];
  if (directFields.includes(errorField)) {
    return [errorField];
  }

  // Context-prefixed field names (e.g., "context.account_id" → "account_id")
  if (errorField.startsWith('context.')) {
    const stripped = errorField.replace('context.', '');
    if (directFields.includes(stripped)) {
      return [stripped];
    }
  }

  return [];
};

export const resolveSubscriberErrorMessage = (
  err: VerifySubscribersResponse[number] | undefined,
  t: (key: string, options?: Record<string, unknown>) => string
): string => {
  const code = classifySubscriberError(err);
  if (code === 'subscriber.generic') {
    const raw = err?.message || err?.error || '';
    if (!raw) {
      return t('campaigns.subscriber_errors.generic');
    }
    // Clean up raw API messages:
    // - Strip campaign type prefix ("payment_recovery campaigns require ...")
    // - Strip "context." prefix from field paths ("context.country_code" → "country_code")
    return raw
      .replace(/^(payment_recovery|marketing)\s+campaigns?\s+/i, '')
      .replace(/\bcontext\./g, '');
  }
  return t(subscriberErrorI18nKey[code]);
};
